import {
  BLOG_PAGE_SIZE,
  blogBasePath,
  blogCategoryTitle,
  blogHomePath,
  blogPageUrl,
  isBlogCategory,
  type BlogLocale,
} from '~/constants/blog'
import { localizedPath } from '~/constants/appLocale'
import type { BlogListResponse } from '~/types'

interface UseBlogListingOptions {
  /** Category slug, or null/undefined for the whole blog. */
  category?: string | null
  /** 1-based page number. */
  page?: number
  /** Which blog: the English one at /blog (default), the Czech one at /cs/blog or the German one at /de/blog. Categories are English only. */
  locale?: BlogLocale
}

/**
 * Shared data + SEO for all blog listing routes:
 *   /blog, /blog/page/N, /blog/category/X, /blog/category/X/page/N,
 *   and the Czech and German blogs' /cs/blog, /de/blog and their /page/N
 *
 * Everything crawler-relevant lives here so the four page components stay thin
 * and cannot drift apart:
 *
 * - data is fetched during SSR, so the article links are in the server HTML
 * - unknown categories and out-of-range pages throw real 404s instead of
 *   rendering an empty list with HTTP 200
 * - each page self-canonicalises. Pointing page N at page 1 is the usual
 *   "fix" for paginated duplicates and it destroys the only crawl path to
 *   everything past the first page.
 */
export async function useBlogListing(options: UseBlogListingOptions = {}) {
  const route = useRoute()
  const router = useRouter()
  const { t } = useI18n()
  const { siteUrl } = useSiteUrl()

  const category = options.category ?? null
  const page = options.page ?? 1
  const locale = options.locale ?? 'en'
  const home = blogHomePath(locale)

  // Validate before fetching: a bad URL should never cost a query.
  if (category !== null && !isBlogCategory(category)) {
    throw createError({ statusCode: 404, statusMessage: 'Unknown blog category', fatal: true })
  }
  if (!Number.isInteger(page) || page < 1) {
    throw createError({ statusCode: 404, statusMessage: 'Invalid page number', fatal: true })
  }

  const basePath = blogBasePath(category, locale)
  const pageUrl = (target: number) => blogPageUrl(basePath, target)

  // Search is a client-side refinement of the full listing. It is read here
  // rather than in onMounted so `?search=` filters server-side too — the
  // SearchAction in our structured data promises exactly that.
  const searchQuery = ref(typeof route.query.search === 'string' ? route.query.search : '')

  /*
   * IMPORTANT: this promise is deliberately NOT awaited until the very end of the
   * function. Every composable below that touches the head (useSeoMeta, useHead,
   * useRobotsRule, useJsonLdGraph) needs the Nuxt instance, and that context is
   * lost after the first `await` inside a composable. useAsyncData hands back its
   * refs synchronously, so the head can be wired to those refs first and awaited
   * afterwards.
   */
  const asyncData = useAsyncData(
    () => `blog-list-${locale}-${category ?? 'all'}-${page}-${searchQuery.value}`,
    () =>
      $fetch<BlogListResponse>('/api/blog/list', {
        query: {
          page,
          limit: BLOG_PAGE_SIZE,
          locale,
          ...(category ? { category } : {}),
          ...(searchQuery.value ? { search: searchQuery.value } : {}),
        },
      }),
    { watch: [searchQuery] },
  )
  const { data } = asyncData

  const posts = computed(() => data.value?.items ?? [])
  const total = computed(() => data.value?.total ?? 0)
  const pageCount = computed(() => data.value?.pageCount ?? 1)
  const isEmpty = computed(() => posts.value.length === 0)

  /*
   * Search results are thin and duplicate the listing — crawl them, don't index them.
   *
   * Two deliberate details:
   *
   * 1. The rule is only set for search pages. Setting an explicit 'index, follow'
   *    otherwise would override nuxt-site-config's `indexable: false` on dev and
   *    Vercel preview deployments, making previews advertise themselves as
   *    indexable while the rest of the site correctly does not.
   * 2. It keys on `searchQuery` only, which comes straight off the route and so is
   *    known synchronously. useRobotsRule resolves its argument once at call time
   *    (it calls toValue immediately and is not reactive), so any condition that
   *    depends on the fetch — "did this come back empty?" — would read as true
   *    here and noindex the entire blog.
   */
  if (searchQuery.value) {
    useRobotsRule('noindex, follow')
  }

  const titleBase = computed(() =>
    category ? t('blog.categoryTitle', { category: blogCategoryTitle(category) }) : t('blog.title'),
  )

  // Each category has its own description, and page N says which page it is,
  // so no two listing URLs share one (a crawl found 22 duplicates).
  const descriptionBase = computed(() =>
    category ? t(`blog.categoryDescriptions.${category}`) : t('blog.metaDescriptionIndex'),
  )
  const description = computed(() =>
    page > 1 ? `${descriptionBase.value} ${t('blog.pageN', { page })}.` : descriptionBase.value,
  )

  useSeoMeta({
    title: () => (page > 1 ? `${titleBase.value} — ${t('blog.pageN', { page })}` : titleBase.value),
    description: () => description.value,
    ogTitle: () => titleBase.value,
    ogDescription: () => description.value,
    ogType: 'website',
    ogSiteName: 'InMyResume',
    ogUrl: () => `${siteUrl}${pageUrl(page)}`,
  })

  useHead(() => ({
    link: [
      // Self-referencing, per page. `key` replaces app.vue's site-wide canonical
      // instead of emitting a second one.
      { rel: 'canonical', key: 'canonical', href: `${siteUrl}${pageUrl(page)}` },
      ...(page > 1
        ? [{ rel: 'prev', key: 'prev', href: `${siteUrl}${pageUrl(page - 1)}` }]
        : []),
      ...(page < pageCount.value
        ? [{ rel: 'next', key: 'next', href: `${siteUrl}${pageUrl(page + 1)}` }]
        : []),
    ],
  }))

  const {
    organizationNode,
    webSiteNode,
    webPageNode,
    breadcrumbNode,
    useJsonLdGraph,
  } = useJsonLd()

  useJsonLdGraph(() => [
    organizationNode(),
    webSiteNode(),
    webPageNode({
      path: pageUrl(page),
      name: titleBase.value,
      description: description.value,
      type: 'CollectionPage',
      hasBreadcrumb: true,
    }),
    breadcrumbNode(pageUrl(page), [
      { name: t('breadcrumbs.home'), path: localizedPath('/', locale) },
      ...(category
        ? [
            { name: t('footer.blog'), path: home },
            { name: blogCategoryTitle(category)! },
          ]
        : [{ name: t('footer.blog') }]),
    ]),
  ])

  /** Search always lands on the global listing, so results are never scoped twice. */
  const submitSearch = (term: string) => {
    searchQuery.value = term
    router.replace({ path: home, query: term ? { search: term } : {} })
  }

  // Everything that needs the Nuxt instance is registered by now, so it is safe
  // to await the fetch and turn its result into HTTP status codes.
  await asyncData

  if (!data.value) {
    throw createError({ statusCode: 502, statusMessage: 'Failed to load blog posts', fatal: true })
  }

  // Page 12 of 11 is a real 404. Page 1 of an empty-but-valid category is not —
  // that URL is legitimate and may have posts tomorrow, so it renders an empty
  // state with a 200 and is kept out of the index by the noindex rule above.
  if (page > 1 && page > data.value.pageCount) {
    throw createError({ statusCode: 404, statusMessage: 'Page not found', fatal: true })
  }

  return {
    posts,
    total,
    page,
    pageCount,
    isEmpty,
    category,
    locale,
    basePath,
    pageUrl,
    searchQuery,
    submitSearch,
    titleBase,
  }
}
